import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { AgGridReact } from 'ag-grid-react';
import { useGridTheme } from '@/config/useGridTheme';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { toast } from 'react-toastify';

export interface MongoColumn {
  fieldName: string;
  type: string;
  csvHeader: string;
}

interface SftpMongoColumnMapDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (columns: MongoColumn[]) => void;
  columns: MongoColumn[];
  destinationName: string;
}

interface MongoColumnMapRow extends MongoColumn {
  id: string;
}

const BSON_DATA_TYPES = [
  'STRING',
  'INT32',
  'INT64',
  'DOUBLE',
  'DECIMAL128',
  'BOOLEAN',
  'DATE_TIME',
  'OBJECT_ID',
  'GUID',
];

const DeleteButtonRenderer: React.FC<ICellRendererParams> = (props) => {
  const handleDelete = () => {
    if (props.context?.deleteRow) {
      props.context.deleteRow(props.node.data.id);
    }
  };

  return (
    <Button
      size="small"
      color="error"
      variant="contained"
      startIcon={<DeleteIcon />}
      onClick={handleDelete}
      sx={{
        minWidth: 'auto',
        fontSize: '0.8125rem',
        py: 0.5,
        px: 1.5,
      }}
    >
      Delete
    </Button>
  );
};

export const SftpMongoColumnMapDialog: React.FC<SftpMongoColumnMapDialogProps> = ({
  open,
  onClose,
  onSave,
  columns,
  destinationName,
}) => {
  const gridRef = useRef<AgGridReact<MongoColumnMapRow>>(null);
  const gridTheme = useGridTheme();
  const [rowData, setRowData] = useState<MongoColumnMapRow[]>([]);
  const [originalData, setOriginalData] = useState<MongoColumnMapRow[]>([]);
  const [nextId, setNextId] = useState(1);

  useEffect(() => {
    if (open && columns) {
      const mappedData = columns.map((col, index) => ({
        ...col,
        id: `existing-${index}`,
      }));
      setRowData(mappedData);
      setOriginalData(mappedData);
      setNextId(mappedData.length + 1);
    }
  }, [columns, open]);

  const columnDefs = useMemo<ColDef<MongoColumnMapRow>[]>(() => [
    {
      field: 'fieldName',
      headerName: 'Field Name',
      editable: true,
      flex: 1,
      cellDataType: 'text',
    },
    {
      field: 'type',
      headerName: 'BSON Type',
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: BSON_DATA_TYPES,
      },
      flex: 1,
    },
    {
      field: 'csvHeader',
      headerName: 'CSV Header',
      editable: true,
      flex: 1,
      cellDataType: 'text',
    },
    {
      headerName: '',
      width: 100,
      cellRenderer: DeleteButtonRenderer,
      editable: false,
      sortable: false,
      filter: false,
    },
  ], []);

  const gridContext = useMemo(() => ({
    deleteRow: (id: string) => {
      setRowData(prev => prev.filter(row => row.id !== id));
    },
  }), []);

  const handleAddRow = () => {
    const newRow: MongoColumnMapRow = {
      id: `new-${nextId}`,
      fieldName: '',
      type: 'STRING',
      csvHeader: '',
    };
    setRowData(prev => [...prev, newRow]);
    setNextId(prev => prev + 1);
  };

  const validateData = (): string[] => {
    const errors: string[] = [];
    const fieldNames = new Set<string>();

    rowData.forEach((row, index) => {
      if (!row.fieldName.trim()) {
        errors.push(`Row ${index + 1}: Field Name is required`);
      } else if (fieldNames.has(row.fieldName)) {
        errors.push(`Row ${index + 1}: Duplicate Field Name "${row.fieldName}"`);
      } else {
        fieldNames.add(row.fieldName);
      }

      if (!row.type) {
        errors.push(`Row ${index + 1}: BSON Type is required`);
      }
    });

    return errors;
  };

  const handleSave = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.stopEditing();
    }

    const errors = validateData();
    if (errors.length > 0) {
      toast.error(
        <Box>
          <Typography variant="subtitle2">Validation Errors:</Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Box>
      );
      return;
    }

    const mongoColumns = rowData.map(({ id, ...col }) => col);
    onSave(mongoColumns);
    onClose();
  };

  const handleClose = () => {
    setRowData(originalData);
    onClose();
  };

  const hasChanges = useMemo(() => {
    if (rowData.length !== originalData.length) return true;

    return rowData.some((row, index) => {
      const original = originalData[index];
      return !original ||
        row.fieldName !== original.fieldName ||
        row.type !== original.type ||
        row.csvHeader !== original.csvHeader;
    });
  }, [rowData, originalData]);

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        MongoDB Column Mapping - {destinationName}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Define how CSV columns map to MongoDB fields. All mappings will be saved together.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddRow}
            size="small"
            sx={{ mt: 1 }}
          >
            Add Column Mapping
          </Button>
        </Box>

        {rowData.length === 0 ? (
          <Alert severity="info">
            No column mappings defined. Click "Add Column Mapping" to create one.
          </Alert>
        ) : (
          <Box
            sx={{
              height: 400,
              width: '100%',
              '& .ag-cell': {
                cursor: 'text',
              },
              '& .ag-cell-focus': {
                border: '1px solid',
                borderColor: 'primary.main',
              },
            }}
          >
            <AgGridReact<MongoColumnMapRow>
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              context={gridContext}
              theme={gridTheme}
              onCellValueChanged={(event) => {
                const updatedData = [...rowData];
                const index = updatedData.findIndex(row => row.id === event.data.id);
                if (index !== -1) {
                  updatedData[index] = event.data;
                  setRowData(updatedData);
                }
              }}
              defaultColDef={{
                resizable: true,
                sortable: true,
                editable: true,
              }}
              animateRows={true}
              rowSelection={{
                mode: 'singleRow',
                checkboxes: false,
                enableClickSelection: true,
              }}
              getRowId={(params) => params.data.id}
              singleClickEdit={true}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} startIcon={<CancelIcon />}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={!hasChanges}
        >
          Save All Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};
